import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

interface ComingSoonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFeedbackClick: () => void;
}

const ComingSoonModal: React.FC<ComingSoonModalProps> = ({ isOpen, onClose, onFeedbackClick }) => {
  // Lock body scroll when modal is open
  React.useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        const scrollY = document.body.style.top;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      };
    }
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fullscreen-modal-overlay"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fullscreen-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Coming Soon</h2>
              <button onClick={onClose} className="close-button">
                <X size={24} />
              </button>
            </div>

            <div className="modal-content coming-soon-modal">
              <div className="coming-soon-content-wrapper">
                <div className="coming-soon-header">
                  <div className="sparkle-icon">✨</div>
                  <h2>Exciting Features Coming Soon!</h2>
                  <p className="coming-soon-subtitle">We're working hard to bring you these amazing features</p>
                </div>
                
                <div className="coming-soon-features">
                  <div className="feature-card">
                    <div className="feature-icon">👥</div>
                    <h3>Community-Powered Insights</h3>
                    <p>Crowdsource real-time data about shopping experiences, parking availability, and crowd levels from our community</p>
                    <span className="feature-tag">Data Collection</span>
                  </div>

                  <div className="feature-card">
                    <div className="feature-icon">🚗</div>
                    <h3>Smart Parking Assistant</h3>
                    <p>Get personalized parking recommendations and suggestions based on your destination, time, and preferences</p>
                    <span className="feature-tag">AI-Powered</span>
                  </div>

                  <div className="feature-card">
                    <div className="feature-icon">🏖️</div>
                    <h3>More Places You'll Love</h3>
                    <p>Discover beaches, tourist attractions, parks, entertainment venues, and hidden gems across Sydney and beyond</p>
                    <span className="feature-tag">Expanding Coverage</span>
                  </div>

                  <div className="feature-card">
                    <div className="feature-icon">💬</div>
                    <h3>Community Reviews & Tips</h3>
                    <p>Share your experiences and read authentic reviews from locals about the best times to visit and insider tips</p>
                    <span className="feature-tag">Social Features</span>
                  </div>

                  <div className="feature-card">
                    <div className="feature-icon">🎯</div>
                    <h3>Personalized Recommendations</h3>
                    <p>Receive tailored suggestions for places to visit based on your preferences, past visits, and crowd tolerance</p>
                    <span className="feature-tag">Machine Learning</span>
                  </div>

                  <div className="feature-card">
                    <div className="feature-icon">📊</div>
                    <h3>Advanced Analytics</h3>
                    <p>Track crowd patterns, predict future crowd levels, and make data-driven decisions about when and where to go</p>
                    <span className="feature-tag">Predictive Insights</span>
                  </div>
                </div>

                <div className="coming-soon-footer">
                  <p>Give us your feedback or contact us for support</p>
                  <button 
                    onClick={() => {
                      onClose();
                      onFeedbackClick();
                    }}
                    className="notify-button"
                  >
                    Send Feedback
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ComingSoonModal;

